import type { Crash } from "../../src/feedback/crash.ts";
import { shownAddress } from "../../src/feedback/shown.ts";
import type { FeedbackDiagnostics } from "./api.ts";

// Feedback and bug reports (features/feedback.md): the requests this page saw fail, and the way any part of the page
// opens the form (Send feedback on the profile and in the trip menu, Report this on every error and failed page, the
// server's own pages by their address). The form itself is Feedback.tsx, over whatever the page shows.

export type FeedbackKind = "bug" | "idea" | "other";

export interface FeedbackAsk {
  kind: FeedbackKind;
  // The error reported (its request id), from a Report this.
  requestId?: string | null;
  // The page it's about, when it isn't this one (the server's wrong gate: the address that wasn't there).
  page?: string | null;
  // From a page that broke (crash.tsx): the crash's error and where in Tripmo's code it happened.
  crash?: Crash | null;
}

// The requests this page saw fail last, newest first: their id (Tripmo's x-request-id, or Cloudflare's ray where the
// box didn't answer), status (0: no answer), path without its query, and when. A few, in memory only.
export interface Failure {
  id: string;
  status: number;
  path: string;
  at: number;
}
const KEPT = 8;
const failures: Failure[] = [];

// A request that failed: noted, if it has an id to find it by (signed out isn't a failure).
export function noteFailure(res: Response | null, url: string, status = res?.status ?? 0): void {
  if (status === 401) return;
  const id = res?.headers.get("x-request-id") ?? res?.headers.get("cf-ray") ?? null;
  if (!id) return;
  let path = url;
  try {
    path = new URL(url, location.href).pathname;
  } catch {}
  failures.unshift({ id: id.slice(0, 64), status, path: shownAddress(path).slice(0, 300), at: Date.now() });
  failures.length = Math.min(failures.length, KEPT);
}

// The request that failed last, if it was within `ms`: what an error shown then is about.
export function lastFailure(ms: number): Failure | null {
  const last = failures[0];
  return last && Date.now() - last.at < ms ? last : null;
}

// Open the form, from anywhere on the page.
const OPEN = "tripmo:feedback";
export function openFeedback(ask: FeedbackAsk): void {
  dispatchEvent(new CustomEvent<FeedbackAsk>(OPEN, { detail: ask }));
}
export function onOpenFeedback(open: (ask: FeedbackAsk) => void): () => void {
  const on = (e: Event) => open((e as CustomEvent<FeedbackAsk>).detail);
  addEventListener(OPEN, on);
  return () => removeEventListener(OPEN, on);
}

// A Report this from one of the server's own pages (the wrong gate, a page that failed) comes as the address
// /?feedback=bug&request=…&page=…, and Feedback's own address, /feedback, as the form over Your trips (Bug first, like
// the other ways in): what it asks, and the address without it (Your trips' own, /, for /feedback).
export function askedByAddress(): { ask: FeedbackAsk; rest: string } | null {
  const params = new URLSearchParams(location.search);
  const own = /^\/feedback\/?$/.test(location.pathname);
  const kind = params.get("feedback") ?? (own ? "bug" : null);
  if (kind !== "bug" && kind !== "idea" && kind !== "other") return null;
  const page = params.get("page");
  const ask: FeedbackAsk = {
    kind,
    requestId: params.get("request")?.slice(0, 64) || null,
    // Only an address on this site.
    page: page?.startsWith("/") && !page.startsWith("//") ? page.slice(0, 2000) : null,
  };
  for (const name of ["feedback", "request", "page"]) params.delete(name);
  const query = params.toString();
  return { ask, rest: `${own ? "/" : location.pathname}${query ? `?${query}` : ""}${location.hash}` };
}

// What helps find it, as the form says it: this page's address, Tripmo's version on it, the browser and the device,
// the time, the requests that failed here (the one reported first), and on a page that broke, the crash. Never anything
// of the trip's but its address, nor a secret an address carries (a join link's code: src/feedback/shown.ts).
export function diagnostics(ask: FeedbackAsk): FeedbackDiagnostics {
  const version = document.querySelector<HTMLMetaElement>('meta[name="tripmo-version"]')?.content;
  const touch = matchMedia("(pointer: coarse)").matches;
  const installed = matchMedia("(display-mode: standalone)").matches;
  const dark = document.documentElement.dataset["theme"]
    ? document.documentElement.dataset["theme"] === "dark"
    : matchMedia("(prefers-color-scheme: dark)").matches;
  const device = [
    `${innerWidth} × ${innerHeight} at ${Math.round(devicePixelRatio * 100) / 100}x`,
    touch ? "touch" : "pointer",
    installed ? "installed" : "in the browser",
    dark ? "dark" : "light",
  ].join(", ");
  const now = new Date();
  const offset = -now.getTimezoneOffset();
  const pad = (n: number) => String(Math.abs(n)).padStart(2, "0");
  const local = new Date(now.getTime() + offset * 60_000).toISOString().slice(0, 19);
  const time = `${local}${offset >= 0 ? "+" : "-"}${pad(Math.trunc(offset / 60))}:${pad(offset % 60)}`;
  let zone: string | undefined;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {}
  const reported = ask.requestId ?? null;
  return {
    page: shownAddress(ask.page ?? `${location.pathname}${location.search}`),
    ...(version ? { version } : {}),
    browser: navigator.userAgent,
    device,
    language: navigator.language,
    time,
    ...(zone ? { time_zone: zone } : {}),
    ...(reported ? { request_id: reported } : {}),
    errors: failures.map((f) => ({
      request_id: f.id,
      status: f.status,
      path: f.path,
      at: new Date(f.at).toISOString(),
    })),
    ...(ask.crash ? { crash: ask.crash } : {}),
  };
}
